import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Home, LayoutDashboard, User, GraduationCap, FileText, Calendar, DollarSign, Scale, ChevronRight, PenTool, Menu, ThumbsUp, Grid3x3, HelpCircle, Brain } from 'lucide-react';

const CollegeJourneyNavigation = ({
  customTabHandler = null,
  showHeader = true  // New prop to control header visibility
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Section color mapping
  const sectionColors = {
    dashboard: { name: 'teal', bg: 'bg-teal-500', text: 'text-teal-500', border: 'border-teal-500', lightBg: 'bg-teal-50' },
    profile: { name: 'azure', bg: 'bg-azure-500', text: 'text-azure-500', border: 'border-azure-500', lightBg: 'bg-azure-50' },
    'strategic-assessment': { name: 'indigo', bg: 'bg-indigo-600', text: 'text-indigo-600', border: 'border-indigo-600', lightBg: 'bg-indigo-50' },
    colleges: { name: 'indigo', bg: 'bg-indigo-500', text: 'text-indigo-500', border: 'border-indigo-500', lightBg: 'bg-indigo-50' },
    essays: { name: 'teal', bg: 'bg-teal-500', text: 'text-teal-500', border: 'border-teal-500', lightBg: 'bg-teal-50' },
    timeline: { name: 'coral', bg: 'bg-coral-500', text: 'text-coral-500', border: 'border-coral-500', lightBg: 'bg-coral-50' },
    recommendations: { name: 'gold', bg: 'bg-gold-500', text: 'text-gold-500', border: 'border-gold-500', lightBg: 'bg-gold-50' },
    'financial-aid': { name: 'azure', bg: 'bg-azure-500', text: 'text-azure-500', border: 'border-azure-500', lightBg: 'bg-azure-50' },
    compare: { name: 'teal', bg: 'bg-teal-500', text: 'text-teal-500', border: 'border-teal-500', lightBg: 'bg-teal-50' }
  };

  // Navigation items configuration
  const navItems = [
    { id: 'dashboard', label: 'Hub', icon: Grid3x3, path: '/college' },
    { id: 'profile', label: 'Profile', icon: User, path: '/college/profile' },
    { id: 'strategic-assessment', label: 'Assessment', icon: Brain, path: '/college/strategic-assessment' },
    { id: 'colleges', label: 'Colleges', icon: GraduationCap, path: '/college/colleges' },
    { id: 'essays', label: 'Essays', icon: FileText, path: '/college/essays' },
    { id: 'timeline', label: 'Timeline', icon: Calendar, path: '/college/timeline' },
    { id: 'recommendations', label: 'Recommendations', icon: ThumbsUp, path: '/college/recommendations' },
    { id: 'financial-aid', label: 'Aid', icon: DollarSign, path: '/college/financial-aid' },
    // Compare removed - now integrated into Colleges section
  ];

  // Sub-tabs configuration for sections that need them
  const subTabsConfig = {
    essays: [
      { id: 'freewriting', label: 'Free Writing', param: 'mode' },
      { id: 'ideation', label: 'Ideation', param: 'mode' },
      { id: 'topics', label: 'Topics', param: 'mode' },
      { id: 'outlines', label: 'Outlines', param: 'mode' },
      { id: 'workspace', label: 'Drafts', param: 'mode' },
      { id: 'slots', label: 'Essay Slots', param: 'mode' },
    ],
    profile: [
      { id: 'core-data', label: 'Academic Profile', param: 'view' },
      { id: 'export-manage', label: 'Export & Manage', param: 'view' }
    ],
    'strategic-assessment': [
      { id: 'overview', label: 'Your Assessment', param: 'view' },
      { id: 'insights', label: 'AI Insights', param: 'view' },
      { id: 'revise', label: 'Revise', param: 'view' },
      { id: 'settings', label: 'Settings', param: 'view' }
    ],
    colleges: [
      { id: 'all-schools', label: 'All Schools', param: 'view' },
      { id: 'my-schools', label: 'My Schools', param: 'view' },
      { id: 'compare', label: 'Compare', param: 'view' },
      { id: 'tier-analysis', label: 'Tier Analysis', param: 'view' },
      { id: 'portfolio-strategy', label: 'Portfolio Strategy', param: 'view' },
    ],
    recommendations: [
      { id: 'all-recommendations', label: 'All Recommendations', param: 'view' },
      { id: 'brag-sheet', label: 'Brag Sheet', param: 'view' },
      { id: 'packet', label: 'Packet', param: 'view' },
      { id: 'request-generator', label: 'Request Generator', param: 'view' },
      { id: 'follow-ups', label: 'Follow Ups', param: 'view' },
      { id: 'thank-you', label: 'Thank You', param: 'view' },
    ],
    'financial-aid': [
      { id: 'financial-aid', label: 'Financial Aid', param: 'view' },
      { id: 'scholarships', label: 'Scholarships', param: 'view' }
    ],
    timeline: [
      { id: 'full-timeline', label: 'Full Timeline', param: 'view' },
      { id: 'by-college', label: 'By College', param: 'view' },
      { id: 'custom-sprint', label: 'Custom Sprint', param: 'view' },
    ]
  };

  // Default sub-tabs for each section
  const defaultSubTabs = {
    essays: 'ideation',
    profile: 'core-data',
    'strategic-assessment': 'overview',
    colleges: 'my-schools',
    recommendations: 'all-recommendations',
    'financial-aid': 'financial-aid',
    timeline: 'full-timeline'
  };

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/college/dashboard') || path === '/college') return 'dashboard';
    if (path.includes('/college/profile')) return 'profile';
    if (path.includes('/college/strategic-assessment') || path.includes('/college/assessment')) return 'strategic-assessment';
    if (path.includes('/college/colleges') || path.includes('/enhanced-strategy/')) return 'colleges';
    if (path.includes('/college/essays')) return 'essays';
    if (path.includes('/college/timeline')) return 'timeline';
    if (path.includes('/college/financial-aid')) return 'financial-aid';
    if (path.includes('/college/recommendations') || path.includes('/college/brag-sheet')) return 'recommendations';
    if (path.includes('/college/compare')) return 'compare';
    return 'colleges'; // Default for individual college pages
  };
  
  const activeTab = getActiveTab();

  // Note: handleBackNavigation and getBreadcrumbs are kept for backward compatibility
  // but not currently used in the new navigation design

  // Handle tab change - use custom handler if provided, otherwise default behavior
  const handleTabChange = (tab) => {
    if (customTabHandler) {
      customTabHandler(tab);
    } else {
      if (tab === 'dashboard') {
        navigate('/college');
      } else {
        navigate(`/college/${tab}`);
      }
    }
  };

  // Get current section info
  const activeItem = navItems.find(item => item.id === activeTab);
  const activeColor = sectionColors[activeTab] || sectionColors.dashboard;
  const currentSubTabs = subTabsConfig[activeTab] || [];

  // Get active sub-tab from URL params, with defaults
  const getActiveSubTab = () => {
    const paramName = activeTab === 'essays' ? 'mode' : 'view';
    const urlValue = searchParams.get(paramName);

    // Special handling for Colleges section routes
    if (activeTab === 'colleges') {
      // Check for special routes in the pathname
      if (location.pathname.includes('/tier-analysis')) {
        return 'tier-analysis';
      }
      if (location.pathname.includes('/portfolio-strategy')) {
        return 'portfolio-strategy';
      }
      if (location.pathname.includes('/compare') && !location.pathname.match(/\/college\/colleges\/[a-f0-9-]{36}/)) {
        return 'compare';
      }
      // Check if we're on an individual college page
      if (/\/college\/colleges\/[a-f0-9-]{36}/.test(location.pathname)) {
        // Don't highlight any sub-tab when on individual college page
        return null;
      }
    }

    // If URL has a value, use it
    if (urlValue) {
      return urlValue;
    }

    // Otherwise, use the default for this section
    return defaultSubTabs[activeTab] || null;
  };

  const activeSubTab = getActiveSubTab();

  // When navigating to a section, set default sub-tab in URL if none exists
  React.useEffect(() => {
    const currentSubTabs = subTabsConfig[activeTab];
    if (currentSubTabs && currentSubTabs.length > 0) {
      const paramName = activeTab === 'essays' ? 'mode' : 'view';
      const currentParam = searchParams.get(paramName);

      // Skip setting default param if on individual college page or special comparison routes
      if (activeTab === 'colleges') {
        const isIndividualCollegePage = /\/college\/colleges\/[a-f0-9-]{36}/.test(location.pathname);
        const isSpecialRoute = location.pathname.includes('/tier-analysis') ||
                               location.pathname.includes('/portfolio-strategy') ||
                               location.pathname.includes('/compare');
        if (isIndividualCollegePage || isSpecialRoute) {
          return;
        }
      }

      // If no param exists, set the default
      if (!currentParam) {
        const defaultValue = defaultSubTabs[activeTab];
        if (defaultValue) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set(paramName, defaultValue);
          navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
        }
      }
    }
  }, [activeTab, searchParams, navigate, location.pathname]);

  // Handle sub-tab navigation
  const handleSubTabChange = (subTabId, paramName) => {
    // Special handling for Colleges section
    if (activeTab === 'colleges') {
      // Check if we're on an individual college page (has UUID in path)
      const isIndividualCollegePage = /\/college\/colleges\/[a-f0-9-]{36}/.test(location.pathname);
      // Check if we're on a special route (compare, tier-analysis, portfolio-strategy)
      const isSpecialRoute = location.pathname.includes('/compare') ||
                             location.pathname.includes('/tier-analysis') ||
                             location.pathname.includes('/portfolio-strategy');

      // Navigate to special routes for tier-analysis and portfolio-strategy
      if (subTabId === 'tier-analysis') {
        navigate('/college/colleges/tier-analysis');
        return;
      } else if (subTabId === 'portfolio-strategy') {
        navigate('/college/colleges/portfolio-strategy');
        return;
      } else if (subTabId === 'compare') {
        navigate('/college/colleges/compare');
        return;
      } else if (isIndividualCollegePage || isSpecialRoute) {
        // If on individual college page OR special route and clicking standard tabs (All Schools, My Schools),
        // go back to base colleges route with the appropriate view parameter
        navigate(`/college/colleges?view=${subTabId}`);
        return;
      }
    }

    // Special handling for Essays section
    if (activeTab === 'essays') {
      // Always navigate cleanly to the base essays route with just the mode parameter
      // This clears any individual essay/slot selections
      navigate(`/college/essays?mode=${subTabId}`);
      return;
    }

    // Special handling for Recommendations section
    if (activeTab === 'recommendations') {
      // Always navigate cleanly to the base recommendations route with just the view parameter
      // This clears any modal state or individual teacher selections
      navigate(`/college/recommendations?view=${subTabId}`);
      return;
    }

    // Default behavior: update query param on current path
    const newParams = new URLSearchParams(searchParams);
    newParams.set(paramName, subTabId);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  return (
    <div className={`${showHeader ? 'sticky top-0' : ''} z-[50] bg-white border-b border-stone-200`}>
      {/* Unified Header - Only show if showHeader is true */}
      {showHeader && (
        <>
          {/* Desktop Header */}
          <div className="hidden sm:block bg-stone-50 px-8 pt-4 pb-2">
            <div className="flex items-center justify-between mb-2">
              {/* Left: Breadcrumb with Hub Icon */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/college')}
                  className="w-10 h-10 rounded-full bg-white border border-slate-900 flex items-center justify-center hover:bg-stone-100 transition-colors"
                  aria-label="Go to Hub"
                >
                  <Home className="w-5 h-5 text-slate-900" />
                </button>
                {activeTab !== 'dashboard' && (
                  <>
                    <ChevronRight className="w-4 h-4 text-stone-400" />
                    <div className="px-2 py-2 flex items-center gap-2">
                      <span className="text-lg text-slate-900 font-semibold">{activeItem?.label}</span>
                      <div className={`w-2 h-2 rounded-full ${activeColor.bg}`} />
                    </div>
                  </>
                )}
              </div>

              {/* Right: Icon Navigation with Expanding Pills */}
              <nav className="flex items-center gap-3">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  const isHovered = hoveredIcon === item.id;
                  const shouldExpand = isHovered;
                  const itemColor = sectionColors[item.id];

                  return (
                    <div key={item.id} className="relative">
                      <button
                        onMouseEnter={() => setHoveredIcon(item.id)}
                        onMouseLeave={() => setHoveredIcon(null)}
                        onClick={() => handleTabChange(item.id)}
                        className={`relative overflow-hidden transition-all duration-300 ease-out shadow-sm ${
                          isActive
                            ? `${itemColor.bg} border-2 border-slate-900`
                            : 'bg-white border border-stone-300 hover:border-slate-500'
                        }`}
                        style={{
                          width: shouldExpand ? '132px' : '40px',
                          height: '40px',
                          borderRadius: '20px',
                        }}
                      >
                        <div className="flex items-center h-full px-2 gap-2">
                          <Icon className={`w-5 h-5 text-light flex-shrink-0 transition-colors justify-center duration-300 ${isActive ? 'text-white' : 'text-slate-600'}`} />
                          <span
                            className={`text-sm text-medium whitespace-nowrap transition-all duration-300 ${
                              isActive ? 'text-white' : 'text-slate-900'
                            }`}
                            style={{
                              opacity: shouldExpand ? 1 : 0,
                              transform: shouldExpand ? 'translateX(0)' : 'translateX(-8px)',
                            }}
                          >
                            {item.label}
                          </span>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="sm:hidden bg-stone-50 px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-1 h-10 rounded-full ${activeColor.bg}`} />
                <div>
                  <div className="text-xs text-slate-600">College Journey</div>
                  <div className="text-sm font-bold text-slate-900">{activeItem?.label}</div>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`mr-10 w-10 h-10 rounded-full border-2 border-slate-900 flex items-center justify-center transition-all ${
                  mobileMenuOpen ? 'bg-slate-900' : 'bg-white'
                }`}
              >
                <Menu className={`w-5 h-5 ${mobileMenuOpen ? 'text-white' : 'text-slate-900'}`} />
              </button>
            </div>

            {/* Mobile Navigation Grid */}
            {mobileMenuOpen && (
              <div className="mb-4 border-2 border-slate-900 rounded-xl bg-white p-3">
                <div className="grid grid-cols-4 gap-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    const itemColor = sectionColors[item.id];

                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          handleTabChange(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className="relative flex flex-col items-center gap-2 p-3 rounded-xl transition-all"
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                          isActive
                            ? `${itemColor.bg} border-2 border-slate-900`
                            : 'bg-stone-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-600'}`} />
                        </div>
                        <span className={`text-xs text-center ${isActive ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                          {item.label}
                        </span>
                        {isActive && (
                          <div className={`absolute top-0 right-0 w-2 h-2 rounded-full ${itemColor.bg}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Sub-Navigation Bar - Show when section has sub-tabs */}
      {currentSubTabs.length > 0 && (
        <div className={`${showHeader ? 'bg-white border-t border-stone-100' : 'bg-white'} px-4 sm:px-8 py-3`}>
          <div className="border-2 border-slate-900 rounded-xl bg-white p-2 sm:p-2.5">
            <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-max sm:min-w-0">
                {currentSubTabs.map((tab) => {
                  const isActive = activeSubTab === tab.id;
                  const isDisabled = tab.disabled;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => !isDisabled && handleSubTabChange(tab.id, tab.param)}
                      disabled={isDisabled}
                      className={`items-center gap-1.5 flex-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-all ${
                        isDisabled
                          ? 'text-stone-400 bg-stone-50 cursor-not-allowed'
                          : isActive
                          ? 'bg-slate-900 text-white font-medium shadow-sm'
                          : 'text-slate-700 hover:bg-stone-100'
                      }`}
                      title={isDisabled ? `Coming in ${tab.comingSoon}` : undefined}
                    >
                      {tab.label}
                      {isDisabled && <HelpCircle className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CollegeJourneyNavigation;